(() => {
  'use strict';
  const instances = new WeakMap();

  class SemaskinProduct {
    constructor(root) {
      this.root = root;
      this.usesMojo = root.dataset.semaOfferSource === 'mojo';
      this.events = new AbortController();
      this.slides = [...root.querySelectorAll('[data-sema-slide]')];
      this.thumbs = [...root.querySelectorAll('[data-sema-thumb]')];
      this.index = 0;
      this.form = root.querySelector('.sema-product-form');
      this.addButton = root.querySelector('[data-sema-atc]');
      this.sticky = root.querySelector('[data-sema-sticky]');
      const options = { signal: this.events.signal };

      root.querySelector('[data-sema-prev]')?.addEventListener('click', () => this.showSlide(this.index - 1), options);
      root.querySelector('[data-sema-next]')?.addEventListener('click', () => this.showSlide(this.index + 1), options);
      this.thumbs.forEach((button) => button.addEventListener('click', () => this.showSlide(Number(button.dataset.semaThumb)), options));
      root.querySelector('.sema-gallery')?.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || !event.target.closest('button')) return;
        event.preventDefault();
        this.showSlide(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        this.thumbs[this.index]?.focus({ preventScroll: true });
      }, options);

      const stage = root.querySelector('.sema-gallery__stage');
      let swipeStart;
      stage?.addEventListener('touchstart', (event) => {
        swipeStart = { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
      }, { ...options, passive: true });
      stage?.addEventListener('touchend', (event) => {
        if (!swipeStart || event.target.closest('button')) return;
        const dx = event.changedTouches[0].clientX - swipeStart.x;
        const dy = event.changedTouches[0].clientY - swipeStart.y;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) this.showSlide(this.index + (dx < 0 ? 1 : -1));
        swipeStart = null;
      }, { ...options, passive: true });

      this.setupPortraitCarousel(options);

      this.form?.addEventListener('change', (event) => {
        if (!this.usesMojo && event.target.matches('.sema-pack input[type="radio"][name="id"]')) this.selectVariant(event.target);
      }, options);
      root.querySelector('[data-sema-sticky-add]')?.addEventListener('click', () => {
        if (!this.form || !this.addButton || this.addButton.disabled) return;
        if (this.usesMojo) {
          const offer = [...this.root.querySelectorAll('[data-mojo-offer]')].find((node) => node.getClientRects().length) || this.form;
          offer.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
          offer.setAttribute('tabindex', '-1');
          offer.focus({ preventScroll: true });
          return;
        }
        this.form.requestSubmit(this.addButton);
      }, options);
      this.form?.addEventListener('submit', (event) => {
        // Respect app interception and accept any successful native variant field.
        if (event.defaultPrevented) return;
        const variantId = new FormData(this.form).get('id');
        if (!variantId || !/^\d+$/.test(String(variantId))) { event.preventDefault(); return; }
        // Never silently buy one tube while the app offer is missing or loading.
        if (this.usesMojo) {
          const offerReady = [...this.root.querySelectorAll('[data-mojo-offer]')].some((node) => node.getClientRects().length && node.querySelector('[data-mojo-tier]'));
          const status = this.form.querySelector('[data-sema-offer-status]');
          if (status) status.hidden = offerReady;
          if (!offerReady) event.preventDefault();
          // Mojo owns async purchase state; no theme lock survives an app error.
          return;
        }
        if (this.submitting) { event.preventDefault(); return; }
        this.submitting = true;
        this.addButton.classList.add('is-submitting');
        this.addButton.setAttribute('aria-busy', 'true');
        this.addButton.querySelector('span').textContent = 'Adding to bag…';
      }, options);
      const requestedVariant = new URL(window.location.href).searchParams.get('variant');
      if (!this.usesMojo && requestedVariant && this.form) {
        const requestedInput = [...this.form.querySelectorAll('input[name="id"]')].find((input) => input.value === requestedVariant && !input.disabled);
        if (requestedInput) {
          requestedInput.checked = true;
          this.selectVariant(requestedInput);
        }
      }
      window.addEventListener('pageshow', () => this.resetSubmit(), options);
      if (this.sticky && this.addButton) {
        this.stickyFrame = null;
        const scheduleStickyUpdate = () => {
          if (this.stickyFrame !== null) return;
          this.stickyFrame = window.requestAnimationFrame(() => {
            this.stickyFrame = null;
            this.sticky.hidden = this.addButton.getBoundingClientRect().bottom >= 0;
          });
        };
        // A hash/focus jump can skip the button's entire viewport intersection.
        window.addEventListener('scroll', scheduleStickyUpdate, { ...options, passive: true });
        window.addEventListener('resize', scheduleStickyUpdate, options);
        window.addEventListener('pageshow', scheduleStickyUpdate, options);
        scheduleStickyUpdate();
      }
    }

    setupPortraitCarousel(options) {
      const track = this.root.querySelector('[data-sema-portrait-track]');
      if (!track) return;
      const previous = this.root.querySelector('[data-sema-portrait-prev]');
      const next = this.root.querySelector('[data-sema-portrait-next]');
      const scrollBehavior = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      const stepSize = () => {
        const cards = track.querySelectorAll('.sema-portrait-card');
        return cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : track.clientWidth;
      };
      const move = (direction) => track.scrollBy({ left: direction * stepSize(), behavior: scrollBehavior() });
      previous?.addEventListener('click', () => move(-1), options);
      next?.addEventListener('click', () => move(1), options);
      track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') {
          track.scrollTo({ left: event.key === 'Home' ? 0 : track.scrollWidth - track.clientWidth, behavior: scrollBehavior() });
        } else move(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      this.portraitFrame = null;
      const scheduleControls = () => {
        if (this.portraitFrame !== null) return;
        this.portraitFrame = window.requestAnimationFrame(() => {
          this.portraitFrame = null;
          const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
          if (previous) previous.disabled = track.scrollLeft <= 2;
          if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
        });
      };
      track.addEventListener('scroll', scheduleControls, { ...options, passive: true });
      window.addEventListener('resize', scheduleControls, options);
      scheduleControls();
    }

    showSlide(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== this.index;
        slide.classList.toggle('is-active', i === this.index);
      });
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === this.index);
        thumb.setAttribute('aria-pressed', String(i === this.index));
      });
      const rail = this.root.querySelector('.sema-gallery__thumbs');
      const activeThumb = this.thumbs[this.index];
      if (rail && activeThumb) {
        const left = activeThumb.offsetLeft;
        if (left < rail.scrollLeft || left + activeThumb.offsetWidth > rail.scrollLeft + rail.clientWidth) {
          rail.scrollTo({ left: Math.max(0, left - (rail.clientWidth - activeThumb.offsetWidth) / 2), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      }
      const count = this.root.querySelector('[data-sema-gallery-count]');
      if (count) count.textContent = `${this.index + 1} / ${this.slides.length}`;
    }

    selectVariant(input) {
      // Only an available, checked native radio may reactivate purchase buttons.
      if (this.usesMojo || input.disabled || !input.checked || this.submitting) return;
      if (this.addButton) {
        this.addButton.disabled = false;
        this.addButton.querySelector('span').textContent = 'Add to bag';
      }
      const stickyAdd = this.root.querySelector('[data-sema-sticky-add]');
      if (stickyAdd) {
        stickyAdd.disabled = false;
        const label = [...stickyAdd.childNodes].find((node) => node.nodeType === 3);
        if (label) label.textContent = 'Add to bag';
      }
      this.root.querySelectorAll('.sema-pack').forEach((pack) => pack.classList.toggle('is-selected', pack.contains(input)));
      this.root.querySelectorAll('[data-sema-price], [data-sema-sticky-price]').forEach((node) => { node.textContent = input.dataset.price; });
      const compare = this.root.querySelector('[data-sema-compare]');
      if (compare) {
        compare.textContent = input.dataset.compare || '';
        compare.hidden = !input.dataset.compare;
      }
      const reference = this.root.querySelector('[data-sema-reference]');
      if (reference) reference.hidden = !input.dataset.compare;
      const savings = this.root.querySelector('[data-sema-savings]');
      if (savings) {
        savings.textContent = input.dataset.savings || '';
        savings.hidden = !input.dataset.savings;
      }
      const title = this.root.querySelector('[data-sema-variant-title]');
      if (title) title.textContent = input.dataset.title === 'Default Title' ? '1 Tube' : input.dataset.title;
      if (window.location.pathname.includes('/products/')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', input.value);
        window.history.replaceState(window.history.state, '', url);
      }
    }

    resetSubmit() {
      this.submitting = false;
      if (!this.addButton) return;
      this.addButton.classList.remove('is-submitting');
      this.addButton.removeAttribute('aria-busy');
      this.addButton.querySelector('span').textContent = this.addButton.disabled ? 'Sold out' : 'Add to bag';
    }

    destroy() {
      this.events.abort();
      if (this.stickyFrame != null) window.cancelAnimationFrame(this.stickyFrame);
      if (this.portraitFrame != null) window.cancelAnimationFrame(this.portraitFrame);
    }
  }

  function initialize(container = document) {
    const roots = container.matches?.('[data-semaskin-pdp]') ? [container] : container.querySelectorAll('[data-semaskin-pdp]');
    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new SemaskinProduct(root));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-semaskin-pdp]').forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
})();
